import { useState, useEffect } from 'react';
import { BarChart3, Calendar, ShoppingCart, TrendingUp, Users, Clock } from 'lucide-react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db, auth } from '../../lib/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useAuthReady } from '../../hooks/useAuthReady';

interface ReportStats {
  totalMealEntries: number;
  totalGroceryLists: number;
  activeGroceryLists: number;
  archivedGroceryLists: number;
  totalOrganizations: number;
  totalUsers: number;
  recentMealEntries: number; // Last 30 days
  recentGroceryLists: number; // Last 30 days
}

interface RecentActivity {
  mealEntries: Array<{
    id: string;
    date: string;
    mealType: string;
    organizationId: string;
    createdAt: string;
   recipeId: string;
   recipeName: string;
  }>;
  groceryLists: Array<{
    id: string;
    name: string;
    status: string;
    organizationId: string;
    createdAt: any;
   organizationName: string;
  }>;
}

export function ReportsView() {
  const [user] = useAuthState(auth);
  const authReady = useAuthReady(true);
  const [stats, setStats] = useState<ReportStats>({
    totalMealEntries: 0,
    totalGroceryLists: 0,
    activeGroceryLists: 0,
    archivedGroceryLists: 0,
    totalOrganizations: 0,
    totalUsers: 0,
    recentMealEntries: 0,
    recentGroceryLists: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity>({
    mealEntries: [],
    groceryLists: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Wait for auth to be ready before fetching
    if (!authReady) {
      console.log('⏳ Waiting for auth to be ready before fetching reports...');
      return;
    }
    
    fetchReportData();
  }, [authReady, user]);

  const fetchReportData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Debug: Check current user authentication
      console.log('Current user:', user?.email);
      console.log('Is god admin:', user?.email === 'admin@menioo.com');
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Calculate date 30 days ago for recent activity
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Initialize arrays
      let mealEntries: any[] = [];
      let groceryLists: any[] = [];
      let organizationsSnapshot: any;
      let usersSnapshot: any;
     let organizationsMap: Record<string, string> = {};
     let recipesMap: Record<string, string> = {};

      try {
        // Fetch meal entries
        console.log('Fetching meal entries...');
        const mealEntriesQuery = query(collection(db, 'mealEntries'));
        const mealEntriesSnapshot = await getDocs(mealEntriesQuery);
        mealEntries = mealEntriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Meal entries fetched:', mealEntries.length);
      } catch (error) {
        console.error('Error fetching meal entries:', error);
      }

      try {
        // Fetch grocery lists
        console.log('Fetching grocery lists...');
        const groceryListsQuery = query(collection(db, 'groceryListsV2'));
        const groceryListsSnapshot = await getDocs(groceryListsQuery);
        groceryLists = groceryListsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Grocery lists fetched:', groceryLists.length);
      } catch (error) {
        console.error('Error fetching grocery lists:', error);
      }

      try {
        // Fetch organizations
        console.log('Fetching organizations...');
        const organizationsQuery = query(collection(db, 'organizations'));
        organizationsSnapshot = await getDocs(organizationsQuery);
       organizationsSnapshot.docs.forEach((doc: any) => {
         organizationsMap[doc.id] = doc.data().name || 'Unknown Organization';
       });
        console.log('Organizations fetched:', organizationsSnapshot.size);
      } catch (error) {
        console.error('Error fetching organizations:', error);
        organizationsSnapshot = { size: 0 };
      }

      try {
        // Fetch users
        console.log('Fetching users...');
        const usersQuery = query(collection(db, 'users'));
        usersSnapshot = await getDocs(usersQuery);
        console.log('Users fetched:', usersSnapshot.size);
      } catch (error) {
        console.error('Error fetching users:', error);
        usersSnapshot = { size: 0 };
      }

     try {
       // Fetch recipes for meal entries
       console.log('Fetching recipes...');
       const recipesQuery = query(collection(db, 'recipes'));
       const recipesSnapshot = await getDocs(recipesQuery);
       recipesSnapshot.docs.forEach((doc: any) => {
         recipesMap[doc.id] = doc.data().name || 'Unknown Recipe';
       });
       console.log('Recipes fetched:', recipesSnapshot.size);
     } catch (error) {
       console.error('Error fetching recipes:', error);
     }
      // Calculate stats
      const totalMealEntries = mealEntries.length;
      const totalGroceryLists = groceryLists.length;
      const activeGroceryLists = groceryLists.filter(list => list.status === 'active').length;
      const archivedGroceryLists = groceryLists.filter(list => list.status === 'archived').length;
      const totalOrganizations = organizationsSnapshot.size;
      const totalUsers = usersSnapshot.size;

      // Recent activity (last 30 days)
      const recentMealEntries = mealEntries.filter(entry => {
        const createdAt = new Date(entry.createdAt);
        return createdAt >= thirtyDaysAgo;
      }).length;

      const recentGroceryLists = groceryLists.filter(list => {
        const createdAt = list.createdAt?.toDate ? list.createdAt.toDate() : new Date(list.createdAt);
        return createdAt >= thirtyDaysAgo;
      }).length;

      setStats({
        totalMealEntries,
        totalGroceryLists,
        activeGroceryLists,
        archivedGroceryLists,
        totalOrganizations,
        totalUsers,
        recentMealEntries,
        recentGroceryLists,
      });

      // Set recent activity (latest 10 of each)
      setRecentActivity({
        mealEntries: mealEntries
          .sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return dateB.getTime() - dateA.getTime(); // Most recent first
          })
          .slice(0, 10)
          .map(entry => ({
           ...entry,
           recipeName: recipesMap[entry.recipeId] || 'Unknown Recipe'
         })),
        groceryLists: groceryLists
          .sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return dateB.getTime() - dateA.getTime(); // Most recent first
          })
          .slice(0, 10)
          .map(list => ({
           ...list,
           organizationName: organizationsMap[list.organizationId] || 'Unknown Organization'
         })),
      });

    } catch (error) {
      console.error('Error fetching report data:', error);
      setError(`Failed to load report data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'Unknown';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  };

  const formatMealType = (mealType: string | null) => {
    if (!mealType) return 'Unspecified';
    const types: Record<string, string> = {
      breakfast: 'Breakfast',
      lunch: 'Lunch',
      snack: 'Snack',
      dinner: 'Dinner',
    };
    return types[mealType] || mealType;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-menioo-green"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-4 text-center">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <BarChart3 className="h-6 w-6 text-menioo-green" />
        <h2 className="text-xl font-display font-bold text-menioo-navy">
          Reports & Analytics
        </h2>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Meal Entries</p>
              <p className="text-2xl font-bold text-menioo-navy">{stats.totalMealEntries.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center">
            <ShoppingCart className="h-8 w-8 text-menioo-green" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Grocery Lists</p>
              <p className="text-2xl font-bold text-menioo-navy">{stats.totalGroceryLists.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Grocery Lists</p>
              <p className="text-2xl font-bold text-menioo-navy">{stats.activeGroceryLists.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-menioo-navy">{stats.totalUsers.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-orange-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Organizations</p>
              <p className="text-2xl font-bold text-menioo-navy">{stats.totalOrganizations.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Recent Meal Entries</p>
              <p className="text-lg font-bold text-menioo-navy">{stats.recentMealEntries}</p>
              <p className="text-xs text-gray-400">Last 30 days</p>
            </div>
            <Clock className="h-6 w-6 text-blue-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Recent Grocery Lists</p>
              <p className="text-lg font-bold text-menioo-navy">{stats.recentGroceryLists}</p>
              <p className="text-xs text-gray-400">Last 30 days</p>
            </div>
            <Clock className="h-6 w-6 text-green-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Archived Lists</p>
              <p className="text-lg font-bold text-menioo-navy">{stats.archivedGroceryLists}</p>
              <p className="text-xs text-gray-400">Completed lists</p>
            </div>
            <ShoppingCart className="h-6 w-6 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Recent Activity Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Meal Entries */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-menioo-navy">Recent Meal Entries</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meal Type
                  </th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Recipe
                 </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentActivity.mealEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatMealType(entry.mealType)}
                    </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                     <div className="text-sm font-medium text-gray-900">{entry.recipeName}</div>
                     <div className="text-sm text-gray-500">ID: {entry.recipeId}</div>
                   </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(entry.createdAt)}
                    </td>
                  </tr>
                ))}
                {recentActivity.mealEntries.length === 0 && (
                  <tr>
                   <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      No recent meal entries found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Grocery Lists */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-menioo-navy">Recent Grocery Lists</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Organization
                 </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentActivity.groceryLists.map((list) => (
                  <tr key={list.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {list.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        list.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {list.status}
                      </span>
                    </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                     <div className="text-sm font-medium text-gray-900">{list.organizationName}</div>
                     <div className="text-sm text-gray-500">ID: {list.organizationId}</div>
                   </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(list.createdAt)}
                    </td>
                  </tr>
                ))}
                {recentActivity.groceryLists.length === 0 && (
                  <tr>
                   <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      No recent grocery lists found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-center">
        <button
          onClick={fetchReportData}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-menioo-green hover:bg-menioo-green-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-menioo-green transition-colors"
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          Refresh Data
        </button>
      </div>
    </div>
  );
}